import { getDeviceId, getDeviceDua } from "@/utils/frontend/device";
import { sendPostRequest, sendPostRequestFromServer } from "@/utils/frontend/sendRequest";

/* axios POST request not working with nextjs api somehow.
import axios from "axios";

export async function authenticateUser2(platform, authData) {
  return await axios.post("/api/authenticate", {
    platform: platform,
    data: authData,
  });
}
*/

export async function updateUser(userId, userBlob, authData, expVersion) {
  const deviceId = getDeviceId();
  const deviceDua = getDeviceDua();
  try {
    return await sendPostRequest("/api/users/update", {
      userId,
      userBlob,
      expVersion,
      deviceId: deviceId,
      deviceDua: deviceDua,
      authData,
    });
  } catch (error) {
    return error.error || "Update user failed.";
  }
}

export async function fetchUser(userId, authData, fallbackFunc, limitVisibility = false) {
  try {
    return await sendPostRequest(
      "/api/users/get",
      {
        userId,
        limitVisibility,
        authData,
        deviceId: getDeviceId(),
      },
      {},
      {
        401: {
          isRedirect: false,
          func: fallbackFunc,
        },
      }
    );
  } catch (error) {
    return "Fetch user info failed.";
  }
}

export async function deleteUser(userId, authData) {
  try {
    return await sendPostRequest("/api/users/delete_user", {
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "delete user failed";
  }
}

export async function fetchTenantInfo(fetchTenantId, userId, authData) {
  try {
    return await sendPostRequest("/api/tenants/get", {
      fetchTenantId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

export async function fetchTenantFeatures(featureTenantId, userId, authData) {
  try {
    return await sendPostRequest("/api/tenants/get_features", {
      featureTenantId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return {};
  }
}

export async function updateTenantInfo(tenantBlob, userId, authData) {
  try {
    return await sendPostRequest("/api/tenants/update", {
      tenantBlob,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error || "Update tenant failure";
  }
}

export async function deleteTenant(deleteTenantId, userId, authData) {
  try {
    return await sendPostRequest("/api/tenants/delete", {
      deleteTenantId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

export async function slackAuthWithLoginAccess(userId, authData) {
  try {
    return await sendPostRequest(
      "/api/oauth/slack_auth_login_access",
      {
        userId,
        authData,
        deviceId: getDeviceId(),
      },
      { credentials: "include" }
    );
  } catch (error) {
    return {
      success: false,
      error,
    };
  }
}

export async function login(values, recaptchaResponse) {
  const deviceId = getDeviceId();
  const deviceDua = getDeviceDua();
  try {
    return await sendPostRequest("/api/users/login", {
      email: values.email,
      password: values.password,
      recaptchaResponse,
      deviceId: deviceId,
      deviceDua: deviceDua,
    });
  } catch (error) {
    return error;
  }
}

export async function consumeOidcCallback(code, state) {
  const deviceDua = getDeviceDua();
  try {
    return await sendPostRequest("/api/users/oidc_callback_login", {
      code,
      state,
      deviceDua,
    });
  } catch (error) {
    return error;
  }
}

export async function logout(userId, authData) {
  try {
    return await sendPostRequest("/api/users/logout", {
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Update user failed.";
  }
}

export async function signup(
  email,
  password,
  defaultLanguage,
  recaptchaResponse,
  userBlob,
  referralCode,
  source
) {
  try {
    const deviceId = getDeviceId();
    const deviceDua = getDeviceDua();
    return await sendPostRequest("/api/users/signup", {
      email,
      password,
      defaultLanguage,
      recaptchaResponse,
      userBlob,
      referralCode,
      source,
      deviceId: deviceId,
      deviceDua: deviceDua,
    });
  } catch (error) {
    return error.errMsg || "signup failed";
  }
}

export async function resetPassword(email, recaptchaResponse) {
  try {
    return await sendPostRequest("/api/users/reset_password", {
      email,
      recaptchaResponse,
    });
  } catch (error) {
    return error.errMsg ? JSON.parse(error.errMsg).description : error.error;
  }
}

export async function changePassword(userId, oldPassword, newPassword, authData) {
  try {
    return await sendPostRequest("/api/users/update_password", {
      userId,
      oldPassword,
      newPassword,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Change password failed.";
  }
}

export async function generateTotpSecret(email) {
  try {
    return await sendPostRequest("/api/users/totp_generate_secret", {
      email,
    });
  } catch (error) {
    return error.error;
  }
}

export async function validateSignupTotp(qrSecret, token) {
  try {
    return await sendPostRequest("/api/users/totp_signup_verify", {
      qrSecret,
      token,
    });
  } catch (error) {
    return error.error;
  }
}

export async function validateLoginTotp(token, code, email, authData) {
  try {
    return await sendPostRequest("/api/users/totp_login_verify", {
      token,
      code,
      email,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error;
  }
}

export async function signupValidation(email, referralCode) {
  try {
    const resp = await sendPostRequest("/api/users/signup_validation", {
      email,
      referralCode,
    });
    return resp;
  } catch (error) {
    return error || "Signup validation failed.";
  }
}

export async function sendVerificationEmail(email, authData) {
  try {
    return await sendPostRequest("/api/users/send_email_verification", {
      email,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return "Email verification failed.";
  }
}

export async function fetchProjects(userId, authData, filterGroup) {
  try {
    return await sendPostRequest("/api/projects/get_all", {
      userId,
      filterGroup,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return [];
  }
}

export async function fetchProjectsByUserId(userId, authData, queryUserId) {
  try {
    return await sendPostRequest("/api/projects/get_all_by_userId", {
      userId,
      queryUserId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return [];
  }
}

export async function fetchBriefProjectsByTenantId(userId, authData, briefTenantId) {
  try {
    return await sendPostRequest("/api/projects/get_brief_by_tenant_id", {
      userId,
      authData,
      briefTenantId,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return [];
  }
}

export async function fetchAllBriefProjectsForCertiK(userId, authData) {
  try {
    return await sendPostRequest("/api/projects/get_all_brief_projects_for_certik", {
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return [];
  }
}

export async function postNewAudit(formData, userId, authData) {
  try {
    return await sendPostRequest("/api/projects/new", {
      formData,
      userId,
      deviceId: getDeviceId(),
      authData,
    });
  } catch (err) {
    console.log(err);
    throw new Error("Failed to post new audit");
  }
}

export async function inviteCollaborator(
  inviteeEmail,
  inviteeRole,
  inviteeCertikTeamId,
  inviterId,
  authData,
  toTenantId = null
) {
  try {
    return await sendPostRequest("api/users/invite_collaborator", {
      inviteeEmail,
      inviteeRole,
      inviteeCertikTeamId,
      inviterId,
      toTenantId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Invite collaborator failed";
  }
}

export async function updateInvitation(inviteeEmail, inviteeRole, certikTeamId, authData) {
  try {
    return await sendPostRequest("api/referrals/update", {
      inviteeEmail,
      inviteeRole,
      certikTeamId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Update invitation failed";
  }
}

export async function resendInvitation(inviteeEmail, inviterId, authData) {
  try {
    return await sendPostRequest("api/users/resend_invitation", {
      inviteeEmail,
      inviterId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Invite collaborator failed";
  }
}

export async function getInvitationForFrontend(referralCode) {
  try {
    return await sendPostRequest("/api/referrals/get", {
      referralCode,
    });
  } catch (error) {
    return error.error || "Get invitation failed";
  }
}

export async function getInvitation(referralCode) {
  // for server-side use
  const requestUrlPrefix =
    process.env.NEXT_PUBLIC_ENV === "staging"
      ? "https://" + process.env.NEXT_PUBLIC_VERCEL_URL
      : process.env.NEXT_PUBLIC_DOMAIN;
  try {
    return await sendPostRequestFromServer(
      requestUrlPrefix + "/api/referrals/get",
      {
        referralCode,
      },
      undefined,
      undefined,
      false
    );
  } catch (error) {
    console.log(JSON.stringify(error));
    return JSON.stringify(error) || "Get invitation failed";
  }
}

export async function deleteInvitation(inviteeEmail, inviterId, authData) {
  try {
    return await sendPostRequest("/api/referrals/delete", {
      inviteeEmail,
      inviterId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Get invitation failed";
  }
}

export async function getCollaborators(userId, authData, fallbackFunc) {
  try {
    return await sendPostRequest(
      "/api/users/get_collaborators",
      {
        userId,
        authData,
        deviceId: getDeviceId(),
      },
      {},
      {
        401: {
          isRedirect: false,
          func: fallbackFunc,
        },
      }
    );
  } catch (error) {
    return error.error || "Get collaborator failed";
  }
}

export async function getPendingCollaboratorsByTenantId(userId, authData, fallbackFunc) {
  try {
    return await sendPostRequest(
      "/api/users/get_pending_collaborators_by_tenantId",
      {
        userId,
        authData,
        deviceId: getDeviceId(),
      },
      {},
      {
        401: {
          isRedirect: false,
          func: fallbackFunc,
        },
      }
    );
  } catch (error) {
    return error.error || "Get pending collaborator by tenant failed";
  }
}

export async function getMultiTypeCollaborates(userId, tenantIdList, authData) {
  try {
    return await sendPostRequest("/api/users/get_multi_type_collaborates", {
      userId,
      tenantIdList,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Get collaborators info failed";
  }
}

export async function getAllDecoratedUsers(userId, authData) {
  try {
    return await sendPostRequest("/api/users/get_all_decorated_users", {
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Get decorated users failed";
  }
}

export async function getNotificationList(userId, authData, type, limit, paginationTimestamp) {
  try {
    return await sendPostRequest("/api/notifications/get_all", {
      userId,
      deviceId: getDeviceId(),
      authData,
      type,
      limit,
      paginationTimestamp,
    });
  } catch (error) {
    return [];
  }
}

export async function getProjectSecurityScoreDetailsByProjectId(
  userId,
  tenantId,
  authData,
  projectId
) {
  try {
    return await sendPostRequest(
      "/api/projects/security-score/get_project_security_score_details",
      {
        projectId,
        tenantId,
        userId,
        authData,
        deviceId: getDeviceId(),
      }
    );
  } catch (error) {
    console.log(error);
    throw new Error(error.errMsg);
  }
}

export async function securityScoreSignOff(
  pendingPeriod,
  projectId,
  userId,
  authData,
  actionDebug = ""
) {
  try {
    return await sendPostRequest("/api/projects/security-score/project_security_score_sign_off", {
      pendingPeriod,
      projectId,
      userId,
      authData,
      deviceId: getDeviceId(),
      actionDebug,
    });
  } catch (error) {
    console.log(error);
    throw new Error(error.errMsg);
  }
}

export async function getUserProjectInfo(userId, projectId, authData) {
  try {
    return await sendPostRequest("/api/projects/fetch_user_project", {
      userId,
      projectId,
      deviceId: getDeviceId(),
      authData,
    });
  } catch (error) {
    console.log(error);
    throw new Error(error.errMsg);
  }
}

export async function getProjectInfoForShareLink(userId, projectId, reportType, authData) {
  try {
    return await sendPostRequest("/api/projects/get_project_for_share_link", {
      userId,
      projectId,
      deviceId: authData.linkId ? "" : getDeviceId(),
      authData,
      reportType,
    });
  } catch (error) {
    return null;
  }
}

export async function deleteNotification(userId, createdAt, purge = false, isSoftArchive = false) {
  try {
    await sendPostRequest("/api/notifications/delete", {
      userId,
      createdAt,
      purge,
      isSoftArchive,
    });
    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
}

export async function updateProject(userId, projectId, projectInfo, authData) {
  try {
    const resp = await sendPostRequest("/api/projects/update", {
      userId,
      projectId,
      projectInfo,
      deviceId: getDeviceId(),
      authData,
    });
    if (resp.result === "success") {
      return true;
    } else {
      return resp;
    }
  } catch (error) {
    console.log(error);
    let errorMessage = error.error;
    if (error.errMsg) {
      errorMessage += ` - ${error.errMsg}`;
    }
    return errorMessage;
  }
}

export async function getProjectReportFromVersion(
  userId,
  authData,
  projectInfo,
  acceleratorProjectId,
  reportVersion
) {
  try {
    const resp = await sendPostRequest("/api/projects/get_project_report_from_version", {
      userId,
      authData,
      deviceId: getDeviceId(),
      projectInfo,
      acceleratorProjectId,
      reportVersion,
    });
    return resp;
  } catch (error) {
    console.log(error);
    let errorMessage = error.error;
    if (error.errMsg) {
      errorMessage += ` - ${error.errMsg}`;
    }
    return errorMessage;
  }
}

export async function updateProjectCommentCheck(userId, projectId, projectInfo, authData) {
  try {
    await sendPostRequest("/api/projects/update_comment_check", {
      userId,
      projectId,
      projectInfo,
      deviceId: getDeviceId(),
      authData,
    });
    return true;
  } catch (error) {
    console.log(error);
    return error.error;
  }
}

export async function fetchProjectCountByStatus(userId, filterGroup, page, authData) {
  try {
    return await sendPostRequest("/api/projects/get_project_count_by_status", {
      userId,
      filterGroup,
      page,
      deviceId: getDeviceId(),
      authData,
    });
  } catch (error) {
    console.log(error);
    return {};
  }
}

export async function updateUserAccess(collaboratorId, collaboratorBlob, expVersion, authData) {
  try {
    return await sendPostRequest("/api/users/update_user_access", {
      collaboratorId,
      collaboratorBlob,
      expVersion,
      authData,
      deviceId: getDeviceId(),
      deviceDua: getDeviceDua(),
    });
  } catch (error) {
    console.log(error);
    return error.error || "Change collaborator's role failed";
  }
}

export async function updateUserAdmin(userId, userBlob, expVersion, authData) {
  try {
    return await sendPostRequest("/api/users/update_user_admin", {
      userId,
      userBlob,
      expVersion,
      authData,
      deviceId: getDeviceId(),
      deviceDua: getDeviceDua(),
    });
  } catch (error) {
    console.log(error);
    return error.error || "Change collaborator's role failed";
  }
}

export async function getLeaderboardInfo(userId, leaderboardId, authData) {
  try {
    return await sendPostRequest(`/api/projects/get_leaderboard_info`, {
      userId,
      leaderboardId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return {};
  }
}

export async function fetchHistoryList(projectId, userId, authData, limit, paginationTimestamp) {
  try {
    return await sendPostRequest("/api/project_histories/fetch_project_history", {
      userId,
      projectId,
      deviceId: getDeviceId(),
      authData,
      limit,
      paginationTimestamp,
    });
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function fetchCommentList(projectId, userId, authData, limit, paginationTimestamp) {
  try {
    return await sendPostRequest("/api/project_comments/get_all", {
      userId,
      projectId,
      deviceId: getDeviceId(),
      authData,
      limit,
      paginationTimestamp,
    });
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function postNewComment(
  projectId,
  userId,
  userName,
  authData,
  text,
  images,
  emailList,
  emailMeta,
  projectType
) {
  try {
    return await sendPostRequest("/api/project_comments/new", {
      userId,
      userName,
      projectId,
      deviceId: getDeviceId(),
      authData,
      text,
      images,
      emailList,
      emailMeta,
      projectType,
    });
  } catch (error) {
    console.log(error);
    return error;
  }
}

export async function uploadImage(userId, fileMeta, fileData, expVersion, authData) {
  try {
    return await sendPostRequest("/api/users/upload_avatar", {
      userId,
      fileMeta,
      fileData,
      expVersion,
      authData,
      deviceId: getDeviceId(),
      deviceDua: getDeviceDua(),
    });
  } catch (error) {
    // TODO: add log
    return error.error || "Update avatar failure";
  }
}

export async function getTenants(userId, authData, fallbackFunc) {
  try {
    return await sendPostRequest(
      "/api/tenants/get_all",
      {
        userId,
        authData,
        deviceId: getDeviceId(),
      },
      {},
      {
        401: {
          isRedirect: false,
          func: fallbackFunc,
        },
      }
    );
  } catch (error) {
    return error.error || "Get tenants failure";
  }
}

export async function createTenant(userId, tenantBlob, tenantAdmin, authData) {
  try {
    return await sendPostRequest("/api/tenants/new", {
      userId,
      tenantBlob,
      tenantAdmin,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error || "Create tenant failure";
  }
}

export async function fetchS3PresignedUrl(
  userId,
  projectTenantId,
  authData,
  keyList,
  command,
  fileType,
  projectId
) {
  try {
    const { urlList } = await sendPostRequest("/api/fetch_s3_presigned_url", {
      userId,
      projectTenantId,
      authData,
      deviceId: getDeviceId(),
      keyList,
      command,
      fileType,
      projectId,
    });
    return urlList;
  } catch (error) {
    return null;
  }
}

export async function deleteProject(userId, authData, projectIdList) {
  try {
    await sendPostRequest("/api/projects/delete", {
      userId,
      authData,
      deviceId: getDeviceId(),
      projectIdList,
    });
    return true;
  } catch (error) {
    return false;
  }
}

export async function getCertikTeams(userId, authData) {
  try {
    return await sendPostRequest("/api/certik_teams/get_all", {
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.errMsg || "Get certik teams failure";
  }
}

export async function getAllCertikAssigneesByRole(role, userId, authData) {
  try {
    return await sendPostRequest("/api/assignee/get_all", {
      role,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

export async function getAssigneesByProjectId(projectId, role, userId, authData, forMarketplace) {
  try {
    return await sendPostRequest("/api/assignee/get_by_project_id", {
      projectId,
      role,
      userId,
      authData,
      deviceId: getDeviceId(),
      forMarketplace,
    });
  } catch (error) {
    return null;
  }
}

export async function getAssigneeProjectIdsByType(assigneeId, type, userId, authData) {
  try {
    return await sendPostRequest("/api/assignee/get_project_ids_by_type", {
      assigneeId,
      type,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return [];
  }
}

export async function createCertikAssigneeAccess(assigneeId, projectId, type, userId, authData) {
  try {
    return await sendPostRequest("/api/assignee/create_access", {
      assigneeId,
      projectId,
      type,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

export async function deleteCertikAssigneeAccess(assigneeId, projectId, userId, authData) {
  try {
    return await sendPostRequest("/api/assignee/delete_access", {
      assigneeId,
      projectId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

export async function fetchProgressList(projectId, userId, authData) {
  try {
    return await sendPostRequest("/api/project_histories/fetch_project_progress", {
      userId,
      projectId,
      deviceId: getDeviceId(),
      authData,
    });
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function fetchFindingCommentList(
  findingCombinationKey,
  projectId,
  userId,
  authData,
  limit,
  paginationTimestamp,
  pageAccess,
  mergeAcrossReports = false
) {
  try {
    return await sendPostRequest("/api/finding_comments/get_all", {
      findingCombinationKey,
      userId,
      projectId,
      deviceId: getDeviceId(),
      authData,
      limit,
      paginationTimestamp,
      pageAccess,
      mergeAcrossReports,
    });
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function certikDelFinding({
  id,
  projectId,
  userId,
  authData,
  findingId,
  deleteAcrossReports = false,
}) {
  try {
    return await sendPostRequest("/api/incremental_findings_report/delete_finding", {
      id,
      projectId,
      authData,
      deviceId: getDeviceId(),
      userId,
      findingId,
      deleteAcrossReports,
    });
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function deleteIncrementalReport({ reportId, userId, authData }) {
  try {
    return await sendPostRequest("/api/incremental_findings_report/delete", {
      reportId,
      authData,
      deviceId: getDeviceId(),
      userId,
    });
  } catch (error) {
    console.log(error);
    return { success: false };
  }
}

export async function replaceIncrementalReportPdf({ reportId, fileUrl, userId, authData }) {
  try {
    return await sendPostRequest("/api/incremental_findings_report/replace_pdf", {
      reportId,
      fileUrl,
      authData,
      deviceId: getDeviceId(),
      userId,
    });
  } catch (error) {
    console.log(error);
    return { success: false };
  }
}

// TODO: better merge getReportCommentCount and getReportCommentCountOnServerSide into one func
export async function getReportCommentCount(reportId, projectId, userId, authData) {
  try {
    return await sendPostRequest("/api/finding_comments/get_count", {
      reportId,
      userId,
      projectId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function postNewFindingComment(
  findingCombinationKey,
  projectId,
  reportId,
  userId,
  authData,
  text,
  images,
  emailList,
  emailMeta,
  acceleratorVersion,
  acceleratorObjectId,
  findingId,
  pageAccess = "audit"
) {
  try {
    return await sendPostRequest("/api/finding_comments/new", {
      findingCombinationKey,
      userId,
      projectId,
      reportId,
      deviceId: getDeviceId(),
      authData,
      text,
      images,
      emailList,
      emailMeta,
      acceleratorVersion,
      findingId,
      acceleratorObjectId,
      pageAccess,
    });
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function postMeetingRequest(
  findingCombinationKey,
  projectId,
  reportId,
  userId,
  authData,
  blob,
  emailMeta,
  acceleratorVersion,
  findingId,
  pageAccess = "audit"
) {
  try {
    return await sendPostRequest("/api/projects/new_request_meeting", {
      findingCombinationKey,
      userId,
      projectId,
      reportId,
      deviceId: getDeviceId(),
      authData,
      blob,
      emailMeta,
      acceleratorVersion,
      findingId,
      pageAccess,
    });
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function getMeetingRequest(
  projectId,
  reportId,
  userId,
  authData,
  pageAccess = "audit"
) {
  try {
    return await sendPostRequest("/api/projects/get_request_meeting", {
      projectId,
      reportId,
      userId,
      deviceId: getDeviceId(),
      authData,
      pageAccess,
    });
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function getCollaboratorsForMention(
  projectId,
  projectTenantId,
  userId,
  authData,
  forReport,
  projectTenantIds
) {
  try {
    return await sendPostRequest("/api/users/get_collaborators_for_mention", {
      projectId,
      projectTenantId,
      userId,
      authData,
      deviceId: getDeviceId(),
      forReport,
      projectTenantIds,
    });
  } catch (error) {
    return error.error || "Get pending collaborators failed";
  }
}

// userId, authData, deviceId, tenantId, nftCollectionId

export async function updateTenantFeatureAccess({
  featureTenantId,
  featureId,
  enabled,
  expVersion,
  userId,
  authData,
}) {
  try {
    return await sendPostRequest("/api/tenants/update_feature", {
      userId,
      authData,
      deviceId: getDeviceId(),
      featureTenantId,
      featureId,
      enabled,
      expVersion,
    });
  } catch (error) {
    console.log(error.message);
    return null;
  }
}

export async function getTenantNameMap(userId, authData) {
  try {
    return await sendPostRequest("/api/tenants/get_tenant_name_map", {
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

// -------Shield------ //

export async function NotifyAssigneeReportReview(
  projectId,
  reportFileName,
  commentCount,
  userId,
  authData,
  actionType
) {
  try {
    return await sendPostRequest("/api/assignee/notify_report_review", {
      projectId,
      reportFileName,
      commentCount,
      userId,
      authData,
      deviceId: getDeviceId(),
      actionType,
    });
  } catch (error) {
    return error;
  }
}

export async function notifyPdfDownload(projectId, reportFileName, reportId, userId, authData) {
  try {
    return await sendPostRequest("/api/assignee/notify_pdf_download", {
      projectId,
      reportFileName,
      reportId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error;
  }
}

export async function getConfig(keyName) {
  try {
    return await sendPostRequest("/api/configs/get", {
      keyName,
      env: process.env.NEXT_PUBLIC_ENV,
    });
  } catch (error) {
    return {};
  }
}

export async function getAllPublicConfigs() {
  try {
    return await sendPostRequest(
      "/api/configs/get_all_public",
      {
        env: process.env.NEXT_PUBLIC_ENV,
      },
      undefined,
      undefined,
      undefined,
      10000
    );
  } catch (error) {
    return {};
  }
}

// for server-side fetching

export async function createShareLink(
  projectId,
  reportFileName,
  toUserEmail,
  userId,
  authData,
  reportType = null,
  readOnly = false
) {
  try {
    return await sendPostRequest("/api/report_share/new", {
      projectId,
      reportFileName,
      toUserEmail,
      userId,
      authData,
      reportType,
      deviceId: getDeviceId(),
      readOnly,
    });
  } catch (error) {
    return {};
  }
}

export async function createShareLinkByProjectId(
  projectId,
  toUserEmail,
  userId,
  authData,
  reportType = null,
  readOnly = false
) {
  try {
    return await sendPostRequest("/api/report_share/new_by_projectId", {
      projectId,
      toUserEmail,
      userId,
      authData,
      reportType,
      deviceId: getDeviceId(),
      readOnly,
    });
  } catch (error) {
    return {};
  }
}

export async function getShareLink(linkId) {
  try {
    return await sendPostRequest("/api/report_share/get", {
      linkId,
    });
  } catch (error) {
    return null;
  }
}

export async function getShareLinksByReport(reportFileName, projectId, userId, authData) {
  try {
    return await sendPostRequest("/api/report_share/get_by_report", {
      reportFileName,
      projectId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return [];
  }
}

export async function getShareLinksByProjectId(projectId, userId, authData, reportType) {
  try {
    return await sendPostRequest("/api/report_share/get_by_projectId", {
      projectId,
      userId,
      authData,
      deviceId: getDeviceId(),
      reportType,
    });
  } catch (error) {
    return [];
  }
}

export async function deleteShareLink(linkId, userId, authData) {
  try {
    return await sendPostRequest("/api/report_share/delete", {
      linkId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return {};
  }
}

export async function signOffReport(
  userId,
  authData,
  reportId,
  projectId,
  projectVersion,
  clientRequestToPublish,
  nextStatus
) {
  try {
    const resp = await sendPostRequest("/api/reports/signoff", {
      reportId,
      projectId,
      projectVersion,
      clientRequestToPublish,
      userId,
      authData,
      deviceId: getDeviceId(),
      nextStatus,
    });
    if (resp.result === "success") {
      return true;
    } else {
      return resp;
    }
  } catch (error) {
    return "Failed to signoff report.";
  }
}

export async function createJiraTicket(userId, authData, projectId, actionType) {
  try {
    return await sendPostRequest("/api/projects/create_jira_ticket", {
      projectId,
      userId,
      authData,
      deviceId: getDeviceId(),
      actionType,
    });
  } catch (error) {
    return null;
  }
}

export async function confirmReport(userId, authData, reportId, projectId, confirmed) {
  try {
    return await sendPostRequest("/api/reports/confirm", {
      reportId,
      projectId,
      userId,
      authData,
      confirmed,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

export async function showSignOff(userId, authData, reportId, projectId, showSignoff) {
  try {
    return await sendPostRequest("/api/reports/showSignOff", {
      reportId,
      projectId,
      userId,
      authData,
      showSignoff,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

export async function validateAcceleratorProjectId(acceleratorProjectId, userId, authData) {
  try {
    return await sendPostRequest("/api/projects/validate_accelerator_project_id", {
      acceleratorProjectId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    console.error(error);
    return;
  }
}

export async function getAcceleratorReportVersions(userId, authData, { acceleratorProjectId }) {
  try {
    return await sendPostRequest("/api/projects/get_accelerator_report_versions", {
      acceleratorProjectId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.errMsg;
  }
}

export async function getTenantTrace(traceTenantId, userId, authData) {
  try {
    return await sendPostRequest("/api/trace/get_by_tenant_id", {
      traceTenantId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

export async function getTenantApiKeys(targetTenantId, userId, authData) {
  try {
    return await sendPostRequest("/api/auth/get_by_tenant_id", {
      targetTenantId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

export async function createApiKey(apiKeyName, apiKeyTenantId, userId, authData) {
  try {
    return await sendPostRequest("/api/auth/new", {
      apiKeyName,
      apiKeyTenantId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error || "Create API Key failure";
  }
}

export async function deleteApiKey(apiKeyId, userId, authData) {
  try {
    return await sendPostRequest("/api/auth/delete", {
      apiKeyId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    console.log(error);
    return error || "Delete API Key failure";
  }
}

export async function updateApiKey(apiKeyId, updateData, userId, authData) {
  try {
    return await sendPostRequest("/api/auth/update", {
      apiKeyId,
      updateData,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    console.log(error);
    return error || "Update API Key failure";
  }
}

export async function createCentralizationMitigation(
  projectId,
  reportFileName,
  findingId,
  mitigationMeta,
  userId,
  authData
) {
  try {
    const resp = await sendPostRequest("/api/centralization_mitigation/new", {
      projectId,
      reportFileName,
      findingId,
      mitigationMeta,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
    if (resp.result === "success") {
      return true;
    } else {
      return resp;
    }
  } catch (error) {
    let errorMessage = error.error;
    if (error.errMsg) {
      errorMessage += ` - ${error.errMsg}`;
    }
    return errorMessage;
  }
}

export async function updateCentralizationMitigationMapping(
  findingCombinationKey,
  targetMitigation,
  projectId,
  reportFileName,
  findingId,
  userId,
  authData
) {
  try {
    const resp = await sendPostRequest("/api/centralization_mitigation/update_mapping", {
      findingCombinationKey,
      targetMitigation,
      projectId,
      reportFileName,
      findingId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
    if (resp.result === "success") {
      return true;
    } else {
      return resp;
    }
  } catch (error) {
    let errorMessage = error.error;
    if (error.errMsg) {
      errorMessage += ` - ${error.errMsg}`;
    }
    return errorMessage;
  }
}

export async function getAllCentralizationMitigation(userId, authData, projectId) {
  try {
    const resp = await sendPostRequest("/api/centralization_mitigation/get_all", {
      userId,
      authData,
      deviceId: getDeviceId(),
      projectId,
    });
    return resp;
  } catch (error) {
    return error.error || "get all centralization mitigation of projectId failed.";
  }
}

export async function getFindingCentralizationMitigation(userId, authData, findingCombinationKey) {
  try {
    const resp = await sendPostRequest("/api/centralization_mitigation/get", {
      userId,
      authData,
      deviceId: getDeviceId(),
      findingCombinationKey,
    });
    return resp;
  } catch (error) {
    return error.error || "get centralization mitigation of finding failed.";
  }
}

export async function submitKycRequest(userId, authData, projectId, projectInfo) {
  try {
    return await sendPostRequest("/api/projects/kyc_request", {
      userId,
      authData,
      projectId,
      projectInfo,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error;
  }
}

/**
 * gets the table item in `client-portal-contentful-ENV` for a given `acceleratorProjectId`
 * @param {string} userId
 * @param {string} authData
 * @param {string} acceleratorProjectId accelerator id
 * @returns {Promise<{ success: true, result: { acceleratorProjectId: string; id: string; name: string; sysId: string; }} | { success: false, reason: string };>}
 */
export async function getContentfulInfoByAccId(userId, tenantId, authData, acceleratorProjectId) {
  try {
    const contentfulInfo = await sendPostRequest("/api/contentful/get", {
      acceleratorProjectId,
      tenantId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
    if (contentfulInfo == null) {
      throw { errMsg: "not found" };
    }
    if (contentfulInfo.errMsg) {
      throw contentfulInfo;
    }
    return {
      success: true,
      result: contentfulInfo,
    };
  } catch (error) {
    return {
      success: false,
      reason: error.errMsg,
    };
  }
}

/**
 *
 * @param {string} userId
 * @param {any} authData
 * @param {string} projectId audit project id
 * @returns
 */
export async function getIncrementalFindingsReport(userId, authData, projectId) {
  try {
    return await sendPostRequest(`/api/incremental_findings_report/get`, {
      userId,
      authData,
      deviceId: getDeviceId(),
      projectId: projectId,
    });
  } catch (error) {
    return {
      success: false,
      reason: error.errMsg,
    };
  }
}

export async function getAllIncrementalFindings(projectId, userId, authData) {
  try {
    return await sendPostRequest(`/api/incremental_findings_report/get_all`, {
      projectId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (err) {
    console.error(err.message);
    return [];
  }
}

export async function getIncrementalFindingHistory(projectId, findingFid, userId, authData) {
  try {
    return await sendPostRequest(`/api/incremental_findings_report/get_finding_history`, {
      projectId,
      findingFid,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (err) {
    console.error(err.message);
    return [];
  }
}

export async function deleteSlackAuth(userId, authData) {
  try {
    return await sendPostRequest("/api/oauth/slack_auth_delete", {
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Delete slack auth failed.";
  }
}

/**
 *
 * @param {string} provider | 'slack'
 * @param {string} type | 'user' or 'tenant'
 * @param {string} userId
 * @param {string} tenantId
 * @param {*} authData
 * @returns
 */
export async function getOauthUrl(provider, type, userId, tenantId, authData) {
  try {
    return await sendPostRequest("/api/oauth/get_oauth_url", {
      authData,
      provider,
      type,
      userId,
      oauthTenantId: tenantId,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Get auth url failed.";
  }
}

export async function getSlackChannelList(type, userId, oauthTenantId, authData) {
  try {
    return await sendPostRequest("/api/oauth/get_slack_channel_list", {
      provider: "slack",
      type,
      userId,
      oauthTenantId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Get slack channel list failed.";
  }
}

export async function getTenantNotificationCustomization(userId, tenantId, authData) {
  try {
    return await sendPostRequest("/api/notifications/get_customization", {
      userId,
      tenantId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Get Notification Customization Failed.";
  }
}

export async function updateTenantNotificationCustomization(
  userId,
  tenantId,
  notificationModule,
  enabledNotificationType,
  channel,
  expVersion,
  authData
) {
  try {
    return await sendPostRequest("/api/notifications/update_customization", {
      userId,
      tenantId,
      notificationModule,
      enabledNotificationType,
      channel,
      expVersion,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Update Notification Customization Failed.";
  }
}

export async function createSlackInteralChannelForTenant(
  userId,
  tenantId,
  slackTenantId,
  channelBlob,
  authData
) {
  try {
    return await sendPostRequest("/api/slack/new_internal_channel", {
      userId,
      tenantId,
      slackTenantId,
      channelBlob,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Create Slack Internal Channel Failed.";
  }
}

export async function postClientRequestToAccelerator(userId, authData, requestToAccString, accId) {
  try {
    return await sendPostRequest("/api/accelerator/clientrequest", {
      userId,
      authData,
      requestToAccString,
      accId,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return error.error || "Post Client Request To Accelerator Failed.";
  }
}

export async function getAddendumProjectInfos(userId, authData, projectId) {
  try {
    return await sendPostRequest("/api/projects/get_addendum_info", {
      userId,
      authData,
      deviceId: getDeviceId(),
      projectId: projectId,
    });
  } catch (error) {
    return error.error || "Get Addendum Project Failed";
  }
}

export async function getProjectNameAndImages(params) {
  try {
    const requestUrlPrefix =
      process.env.NEXT_PUBLIC_ENV == "development"
        ? process.env.NEXT_PUBLIC_DOMAIN
        : "https://" + process.env.NEXT_PUBLIC_VERCEL_URL;
    return await sendPostRequestFromServer(
      requestUrlPrefix + "/api/projects/get_project_name_and_images",
      params,
      undefined,
      undefined,
      false
    );
  } catch (error) {
    return {};
  }
}

export async function fetchUserActionsByProjectId(projectId, userId, authData) {
  try {
    return await sendPostRequest("/api/users/get_all_user_actions_by_projectId", {
      projectId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (err) {
    console.error(err.message);
    return null;
  }
}

export async function fetchUserActionsByTenantId(actionTenantId, userId, authData) {
  try {
    return await sendPostRequest("/api/users/get_all_user_actions_by_tenantId", {
      actionTenantId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (err) {
    console.error(err.message);
    return null;
  }
}

export async function fetchUserActionsByUserId(userId, authData, limit, lastId) {
  try {
    return await sendPostRequest("/api/users/get_user_actions_by_userId", {
      userId,
      authData,
      deviceId: getDeviceId(),
      limit,
      lastId,
    });
  } catch (err) {
    console.error(err.message);
    return null;
  }
}

export async function checkUserSession(userId, authData) {
  try {
    return await sendPostRequest("/api/users/check_session_status", {
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    return null;
  }
}

export async function getEnvConfig(key, env, userId, authData) {
  try {
    return await sendPostRequest("/api/envs/getMeta", {
      key,
      env,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (err) {
    console.error(err.message);
    return {};
  }
}

export async function createOrUpdateFindingFeedback(authData, userId, feedback) {
  try {
    return await sendPostRequest("/api/findings/feedback/create_or_update", {
      feedback,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (err) {
    console.error(err.message);
    return {};
  }
}

export async function getFindingFeedbackByReportId(authData, userId, reportId) {
  try {
    return await sendPostRequest("/api/findings/feedback/get_by_report_id", {
      reportId,
      userId,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (err) {
    console.error(err.message);
    return {};
  }
}

export async function inviteCertiKBotToInternalChannel(channelId, userId, authData) {
  try {
    return await sendPostRequest("/api/oauth/invite_bot_to_certik_channel", {
      channelId,
      userId,
      deviceId: getDeviceId(),
      authData,
    });
  } catch (error) {
    console.log(error);
    return;
  }
}

export async function getEmblemPreviewCode(
  userId,
  authData,
  projectId,
  emblemDesign = "card",
  emblemTheme = "light"
) {
  try {
    return await sendPostRequest(
      `/api/cross_sale/get_emblem_preview_code`,
      {
        userId,
        projectId,
        authData,
        emblemDesign,
        emblemTheme,
        deviceId: getDeviceId(),
      },
      {},
      null,
      true
    );
  } catch (error) {
    console.log(error);
    return;
  }
}

export async function requestEmblem({ projectId, userId, tenantId, projectVersion, authData }) {
  try {
    return await sendPostRequest(`/api/cross_sale/request_emblem`, {
      projectId,
      userId,
      tenantId,
      projectVersion,
      authData,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    console.log(error);
    return;
  }
}

// Finding read status
export async function markFindingAsRead(
  projectId,
  reportId,
  findingId,
  userId,
  authData,
  tenantId
) {
  try {
    return await sendPostRequest("/api/findings/mark_read", {
      projectId,
      reportId,
      findingId,
      userId,
      authData,
      tenantId,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    console.log(error);
    return;
  }
}

export async function getFindingReadStatus(projectId, reportId, userId, authData, tenantId) {
  try {
    return await sendPostRequest("/api/findings/get_read_status", {
      projectId,
      reportId,
      userId,
      authData,
      tenantId,
      deviceId: getDeviceId(),
    });
  } catch (error) {
    console.log(error);
    return;
  }
}

// Optional subscription field
